import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, Subject, interval, of } from 'rxjs';
import { takeUntil, take, switchMap, catchError } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { DateTime } from 'luxon';
import { saveAs } from 'file-saver';

export interface ExportJob {
    jobId: string;
    type: 'orders';
    status: 'queued' | 'active' | 'completed' | 'failed';
    progress: number;
    message: string;
    result?: {
        recordCount: number;
        fileSizeMB: string;
        fileName: string;
    };
    error?: string;
}

@Injectable({
    providedIn: 'root'
})
export class ExportService {
    private _currentJob: BehaviorSubject<ExportJob | null> = new BehaviorSubject(null);
    private _stopPolling: Subject<void> = new Subject<void>();

    constructor(private _httpClient: HttpClient) {}

    get currentJob$(): Observable<ExportJob | null> {
        return this._currentJob.asObservable();
    }

    get isExporting(): boolean {
        const job = this._currentJob.value;
        return job !== null && job.status !== 'completed' && job.status !== 'failed';
    }

    exportOrders(params: {
        search?: string;
        startDate?: Date;
        endDate?: Date;
        companyId?: string;
        charged?: boolean;
        status?: string;
    }): void {
        if (this.isExporting) {
            return;
        }

        const body: any = {
            search: params.search || ''
        };

        if (params.startDate) {
            body.startDate = this.formatDate(params.startDate);
        }
        if (params.endDate) {
            body.endDate = this.formatDate(params.endDate);
        }
        if (params.companyId) {
            body.companyId = params.companyId;
        }
        if (params.charged !== undefined && params.charged !== null) {
            body.charged = params.charged;
        }
        if (params.status) {
            body.status = params.status;
        }

        // Set initial state
        this._currentJob.next({
            jobId: '',
            type: 'orders',
            status: 'queued',
            progress: 0,
            message: 'Starting export...'
        });

        this._httpClient.post<{ jobId: string }>(
            environment.api + 'orders/csv',
            body
        ).pipe(
            catchError(error => {
                console.error('Error starting export:', error);
                this._currentJob.next({
                    jobId: '',
                    type: 'orders',
                    status: 'failed',
                    progress: 0,
                    message: 'Failed to start export',
                    error: error.message || 'Unknown error'
                });
                return of(null);
            })
        ).subscribe(response => {
            if (!response) return;

            this._currentJob.next({
                jobId: response.jobId,
                type: 'orders',
                status: 'queued',
                progress: 0,
                message: 'Export queued'
            });

            this.pollJobStatus(response.jobId);
        });
    }

    dismissJob(): void {
        this._currentJob.next(null);
    }

    private pollJobStatus(jobId: string): void {
        this._stopPolling = new Subject<void>();

        interval(2000).pipe(
            takeUntil(this._stopPolling),
            take(150),
            switchMap(() => this._httpClient.get<any>(
                environment.api + 'orders/csv/status/' + jobId
            )),
            catchError(error => {
                console.error('Error polling status:', error);
                return of(null);
            })
        ).subscribe(status => {
            console.log('Export status response:', status);

            if (!status) {
                this._stopPolling.next();
                this._currentJob.next({
                    ...this._currentJob.value!,
                    status: 'failed',
                    message: 'Failed to get export status',
                    error: 'Connection error'
                });
                return;
            }

            const currentJob = this._currentJob.value;
            if (!currentJob) return;

            console.log('Updating job with progressMessage:', status.progressMessage);

            if (status.state === 'completed') {
                this._stopPolling.next();
                this._currentJob.next({
                    ...currentJob,
                    status: 'completed',
                    progress: 100,
                    message: status.progressMessage || `Export complete: ${status.result?.recordCount} records`,
                    result: status.result
                });
                this.downloadFile(jobId, status.result?.fileName || 'orders_export.xlsx');
            } else if (status.state === 'failed') {
                this._stopPolling.next();
                this._currentJob.next({
                    ...currentJob,
                    status: 'failed',
                    message: 'Export failed',
                    error: status.error?.message || status.error || 'Unknown error'
                });
            } else {
                this._currentJob.next({
                    ...currentJob,
                    status: status.state === 'active' ? 'active' : 'queued',
                    progress: status.progress || 0,
                    message: status.progressMessage || (status.state === 'active' ? 'Processing...' : 'Waiting in queue...')
                });
            }
        });
    }

    private downloadFile(jobId: string, filename: string): void {
        this._httpClient.get<any>(
            environment.api + 'orders/csv/download/' + jobId
        ).pipe(
            catchError(error => {
                console.error('Error downloading file:', error);
                const currentJob = this._currentJob.value;
                if (currentJob) {
                    this._currentJob.next({
                        ...currentJob,
                        message: 'Export complete but download failed',
                        error: 'Download failed'
                    });
                }
                return of(null);
            })
        ).subscribe(response => {
            if (!response) return;

            // Open the pre-signed URL directly to trigger download
            if (response.downloadUrl) {
                window.open(response.downloadUrl, '_blank');
            }

            const currentJob = this._currentJob.value;
            if (currentJob) {
                this._currentJob.next({
                    ...currentJob,
                    message: `Downloaded: ${response.fileName || filename}`
                });
            }
        });
    }

    private formatDate(dateValue: any): string | null {
        if (!dateValue) return null;

        if (typeof dateValue === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateValue)) {
            return dateValue;
        }

        if (DateTime.isDateTime(dateValue)) {
            return dateValue.toFormat('yyyy-MM-dd');
        }

        if (dateValue instanceof Date) {
            const year = dateValue.getFullYear();
            const month = String(dateValue.getMonth() + 1).padStart(2, '0');
            const day = String(dateValue.getDate()).padStart(2, '0');
            return `${year}-${month}-${day}`;
        }

        return null;
    }
}
